import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useCurrentUserProfile,
  useLeaderboard,
  type LeaderboardRow,
  type LeaderboardScope,
} from '@busy/api';
import { useT } from '../../lib/i18n';
import { deriveNicknameFromCounts } from '../../lib/nickname';
import { missingRequired } from '../../lib/profileFields';

// Collapsible leaderboard surface for the Achievements page. Shows the top 10
// barbers in the viewer's chosen scope (50-mile radius / state / country),
// with the viewer's row pinned at the bottom when they're outside the top 10.
//
// Privacy: name masking is enforced server-side by leaderboard_top — when a
// row's first_name is null, the user has share_name_on_leaderboard=false and
// we render "Hidden barber" with a small lock icon. Nickname, rank, and
// counts are still shown.
//
// Persisted state: open/closed flag in localStorage so a returning viewer
// sees the panel the same way they left it. Default is collapsed (matches
// the gallery sections; saves screen space when the user just wants to
// review their badges).

const STORAGE_OPEN_KEY = 'busy.leaderboard.open';
const STORAGE_SCOPE_KEY = 'busy.leaderboard.scope';

const SCOPES: LeaderboardScope[] = ['area', 'state', 'country'];

function loadOpen(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(STORAGE_OPEN_KEY) === '1';
}
function saveOpen(open: boolean) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_OPEN_KEY, open ? '1' : '0');
  } catch {
    /* quota / unavailable — silent */
  }
}
function loadScope(): LeaderboardScope {
  if (typeof window === 'undefined') return 'area';
  const v = window.localStorage.getItem(STORAGE_SCOPE_KEY) as LeaderboardScope | null;
  return v && SCOPES.includes(v) ? v : 'area';
}
function saveScope(scope: LeaderboardScope) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_SCOPE_KEY, scope);
  } catch {
    /* quota / unavailable — silent */
  }
}

export function LeaderboardPanel() {
  const t = useT();
  const navigate = useNavigate();
  const profile = useCurrentUserProfile();
  const [open, setOpen] = useState<boolean>(() => loadOpen());
  const [scope, setScope] = useState<LeaderboardScope>(() => loadScope());

  function toggle() {
    const next = !open;
    setOpen(next);
    saveOpen(next);
  }
  function pickScope(s: LeaderboardScope) {
    setScope(s);
    saveScope(s);
  }

  const profileMissing = profile.data
    ? missingRequired({
        first_name: profile.data.first_name,
        zip: profile.data.zip,
        years_cutting: profile.data.years_cutting,
      })
    : [];
  const profileIncomplete = profile.data && profileMissing.length > 0;

  // Don't fire the RPC until the profile's complete — saves a round trip and
  // keeps the empty state honest (the RPC returns nothing for incomplete
  // profiles anyway, but we don't want to render a "no one nearby" message
  // when the real cause is missing zip).
  const lb = useLeaderboard(scope, 10);
  const rows = lb.data ?? [];
  const topRows = rows.filter((r) => r.in_top);
  const viewerPinned = rows.find((r) => r.is_self && !r.in_top) ?? null;

  // Country label is dynamic — locked to USA in v1 but reads from
  // users.country so the eventual international rollout is a one-line
  // change to the data, not the UI.
  const countryLabel =
    profile.data?.country === 'US' ? 'USA' : profile.data?.country ?? '';

  return (
    <section className="space-y-3">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls="leaderboard-content"
        className="w-full flex items-center gap-3 text-left rounded-xl border border-line bg-surface px-3 py-2.5 hover:border-navy-900/30 dark:hover:border-gold-500/30 transition-colors"
      >
        <TrophyIcon className="w-4 h-4 text-gold-500" />
        <span className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-ink-700">
          {t('leaderboard.title')}
        </span>
        <span className="ml-auto flex items-center gap-2.5">
          <span className="text-[11px] font-semibold text-ink-500">
            {t(`leaderboard.scope.${scope}`, { country: countryLabel })}
          </span>
          <ChevronDownIcon
            className={`w-3.5 h-3.5 text-ink-500 transition-transform ${open ? '' : '-rotate-90'}`}
          />
        </span>
      </button>

      {!open ? null : (
        <div id="leaderboard-content" className="space-y-3">
          {/* Scope toggle — segmented control. Country is enabled but only
              shows USA for v1; the label uses users.country directly so it
              automatically updates when international support lands. */}
          <div className="flex items-center gap-1.5 rounded-xl bg-bg-alt p-1 border border-line">
            {SCOPES.map((s) => {
              const active = s === scope;
              const label = t(`leaderboard.scope.${s}`, { country: countryLabel });
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => pickScope(s)}
                  className={`flex-1 h-9 px-3 rounded-lg text-xs font-bold transition-colors ${
                    active
                      ? 'bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 shadow-sm'
                      : 'text-ink-700 hover:bg-line/60'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {profileIncomplete ? (
            <ProfileGate onCta={() => navigate('/settings')} t={t} />
          ) : lb.loading ? (
            <SkeletonRows />
          ) : lb.error ? (
            <p className="text-sm text-red-600 px-1">
              {t('leaderboard.error', { message: lb.error })}
            </p>
          ) : topRows.length === 0 ? (
            <p className="text-sm text-ink-500 px-1 py-3 text-center">
              {t('leaderboard.empty')}
            </p>
          ) : (
            <ul className="rounded-2xl border border-line bg-surface overflow-hidden divide-y divide-line">
              {topRows.map((row) => (
                <li key={row.user_id}>
                  <Row row={row} />
                </li>
              ))}
              {viewerPinned && (
                <>
                  <li className="bg-bg-alt px-3 py-1.5 text-[10px] tracking-[0.16em] uppercase font-extrabold text-ink-500">
                    {t('leaderboard.yourRow')}
                  </li>
                  <li>
                    <Row row={viewerPinned} />
                  </li>
                </>
              )}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function Row({ row }: { row: LeaderboardRow }) {
  const t = useT();
  const isMasked = row.first_name == null;
  const nick = deriveNicknameFromCounts({
    streak: row.count_streak,
    clients: row.count_clients,
    cuts: row.count_cuts,
    revenue: row.count_revenue,
    lessons: row.count_lessons,
    transformations: row.count_transformations,
    timekeeper: row.count_timekeeper,
  });
  return (
    <div
      className={`flex items-center gap-3 px-3 py-3 ${
        row.is_self ? 'bg-gold-500/10 dark:bg-gold-500/[0.06]' : ''
      }`}
    >
      <RankChip rank={row.rank} ariaLabel={t('leaderboard.rankAria', { rank: row.rank })} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          {isMasked && <LockIcon className="w-3 h-3 text-ink-500 shrink-0" />}
          <span className="text-sm font-bold truncate">
            {isMasked ? t('leaderboard.hidden') : row.first_name}
          </span>
          {row.is_self && (
            <span className="text-[10px] tracking-[0.16em] uppercase font-extrabold text-gold-600 dark:text-gold-400 shrink-0">
              {t('leaderboard.you')}
            </span>
          )}
        </div>
        <div className="text-[12px] text-ink-500 italic truncate">{t(nick.key)}</div>
      </div>
      <div className="shrink-0 text-right">
        <div className="text-sm font-bold tabular-nums">{row.total_badges}</div>
        <div className="text-[10px] tracking-[0.14em] uppercase font-extrabold text-ink-500">
          {t('leaderboard.badges')}
        </div>
        <div className="text-[11px] text-ink-500 mt-0.5 tabular-nums">
          {t('leaderboard.tiersCount', { n: row.total_tiers })}
        </div>
      </div>
    </div>
  );
}

// Rank chip — top three places get a podium icon (gold crown, silver
// medal, bronze medal) with the rank number overlaid in the middle.
// Ranks 4+ render a plain numbered chip so the visual treatment makes
// the podium pop without the rest of the list competing with it.
function RankChip({ rank, ariaLabel }: { rank: number; ariaLabel: string }) {
  // White-on-color across all three medals so the rank number reads
  // consistently against gold / silver / bronze. A small dark text
  // shadow keeps the digit legible on the brighter gold and silver
  // fills (where pure white can wash out against a high-luminance
  // background).
  const podiumNumberClass =
    'relative text-[11px] font-extrabold text-white leading-none tabular-nums drop-shadow-[0_1px_2px_rgba(0,0,0,0.65)]';
  if (rank === 1) {
    return (
      <span
        className="relative shrink-0 w-9 h-9 grid place-items-center"
        aria-label={ariaLabel}
        role="img"
      >
        <CrownFilledIcon className="absolute inset-0 w-9 h-9 text-gold-500 drop-shadow-[0_1px_1px_rgba(0,0,0,0.25)]" />
        <span className={`${podiumNumberClass} translate-y-[2px]`}>1</span>
      </span>
    );
  }
  if (rank === 2) {
    return (
      <span
        className="relative shrink-0 w-9 h-9 grid place-items-center"
        aria-label={ariaLabel}
        role="img"
      >
        <MedalFilledIcon className="absolute inset-0 w-9 h-9 text-zinc-400 drop-shadow-[0_1px_1px_rgba(0,0,0,0.25)]" />
        <span className={`${podiumNumberClass} translate-y-[3px]`}>2</span>
      </span>
    );
  }
  if (rank === 3) {
    return (
      <span
        className="relative shrink-0 w-9 h-9 grid place-items-center"
        aria-label={ariaLabel}
        role="img"
      >
        <MedalFilledIcon className="absolute inset-0 w-9 h-9 text-amber-700 drop-shadow-[0_1px_1px_rgba(0,0,0,0.25)]" />
        <span className={`${podiumNumberClass} translate-y-[3px]`}>3</span>
      </span>
    );
  }
  return (
    <span
      className="shrink-0 w-9 h-9 rounded-lg grid place-items-center text-sm font-extrabold tabular-nums bg-bg-alt text-ink-700"
      aria-label={ariaLabel}
    >
      {rank}
    </span>
  );
}

function ProfileGate({ onCta, t }: { onCta: () => void; t: ReturnType<typeof useT> }) {
  return (
    <div className="rounded-2xl border border-gold-500/40 bg-gradient-to-br from-navy-900 to-navy-800 text-white p-5 text-center">
      <div className="text-[11px] tracking-[0.16em] uppercase font-extrabold text-gold-400 mb-1">
        {t('leaderboard.gate.eyebrow')}
      </div>
      <p className="text-sm leading-snug max-w-xs mx-auto mb-3">
        {t('leaderboard.gate.body')}
      </p>
      <button
        type="button"
        onClick={onCta}
        className="h-10 px-4 rounded-lg bg-gold-500 text-navy-900 text-xs font-bold hover:bg-gold-400 transition-colors"
      >
        {t('leaderboard.gate.cta')}
      </button>
    </div>
  );
}

function SkeletonRows() {
  return (
    <ul className="rounded-2xl border border-line bg-surface overflow-hidden divide-y divide-line">
      {Array.from({ length: 5 }).map((_, i) => (
        <li key={i} className="flex items-center gap-3 px-3 py-3">
          <span className="w-8 h-8 rounded-lg bg-bg-alt animate-pulse" />
          <div className="flex-1 space-y-1.5">
            <span className="block h-3 w-24 rounded bg-bg-alt animate-pulse" />
            <span className="block h-2.5 w-32 rounded bg-bg-alt animate-pulse" />
          </div>
          <span className="w-8 h-6 rounded bg-bg-alt animate-pulse" />
        </li>
      ))}
    </ul>
  );
}

// Solid-filled crown silhouette used as the 1st-place rank chip
// background. The number 1 is overlaid via the parent grid; the crown
// fills currentColor (gold) so the host span controls the tone.
function CrownFilledIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 32 32"
      fill="currentColor"
      aria-hidden
    >
      {/* crown body — three peaks with a flat base */}
      <path d="M3 22 L5 11 L10.5 16 L16 7 L21.5 16 L27 11 L29 22 L3 22 Z" />
      {/* base rim */}
      <rect x="3" y="22" width="26" height="3.5" rx="0.5" />
      {/* small jewel circles on each peak */}
      <circle cx="5" cy="10" r="1.4" opacity="0.85" />
      <circle cx="16" cy="6.5" r="1.6" opacity="0.85" />
      <circle cx="27" cy="10" r="1.4" opacity="0.85" />
    </svg>
  );
}

// Solid-filled medal with two ribbons hanging from the top — used for
// 2nd (silver) and 3rd (bronze) place. Same currentColor pattern as the
// crown; the number sits on the disc via the parent grid.
function MedalFilledIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 32 32"
      fill="currentColor"
      aria-hidden
    >
      {/* left ribbon */}
      <path d="M9 3 L13.5 14 L9 16 L4.5 6 Z" opacity="0.65" />
      {/* right ribbon */}
      <path d="M23 3 L18.5 14 L23 16 L27.5 6 Z" opacity="0.65" />
      {/* medal disc */}
      <circle cx="16" cy="20" r="9" />
      {/* inner ring highlight for depth */}
      <circle cx="16" cy="20" r="7" opacity="0.75" />
    </svg>
  );
}

function TrophyIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M6 9H4a2 2 0 0 1-2-2V4h4" />
      <path d="M18 9h2a2 2 0 0 0 2-2V4h-4" />
      <path d="M6 4h12v6a6 6 0 0 1-12 0V4z" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

